// /mnt/data/PresensiCardAgendaInfo.js
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { computeEventStats } from "../utils/presensiUtils";

function formatDateShort(tsOrIso) {
  if (!tsOrIso) return "";
  try {
    const d = new Date(tsOrIso);
    if (isNaN(d)) return String(tsOrIso).slice(0, 10);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (e) {
    return String(tsOrIso).slice(0, 10);
  }
}

export default function PresensiCardAgendaInfo({
  disabled,
  agenda,
  selectedEventId,
  onSelectEvent,
  showDonation = true,
  lastExportUrl
}) {
  // normalize area to a string label (handles string, populated { data: {...} } or object)
  const _areaObj = agenda?.area?.data ?? agenda?.area;
  const areaLabel = typeof _areaObj === "string"
    ? _areaObj
    : (_areaObj?.name ?? _areaObj?.shortname ?? _areaObj?.title ?? _areaObj?.code ?? (_areaObj?.id ? String(_areaObj.id) : "") ?? "");  
  const events = (agenda.events || []).map(ev => ({
    ...ev,
    participants: (ev.participants || []).filter(p => !p.deleted)
  }));

  const preRegs = agenda.preRegistrations || [];
  const { perEvent, totals, preTotals } = computeEventStats(events, preRegs);

  // responsive: detect mobile (match ContentLayout breakpoint)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 875);
  const [expanded, setExpanded] = useState(!isMobile); // collapsed on mobile by default

  useEffect(() => {
    function onResize() {
      const mobileNow = window.innerWidth < 875;
      setIsMobile(mobileNow);
      if (!mobileNow) setExpanded(true); // always expanded on desktop
      if (mobileNow) setExpanded(false); // collapse when entering mobile
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const containerStyle = {
    padding: "1rem",
    borderRadius: "0.6rem",
    marginBottom: "0.6rem",
    background: "#fff",
    border: "1px solid #eee",
    fontSize: "0.9375rem",
  };

  return (
    <div style={containerStyle}>
      {/* HEADER - compact on mobile */}
      <div style={{ paddingBottom: "0.5rem", borderBottom: expanded ? "1px solid rgba(0,0,0,0.04)" : "none", marginBottom: expanded ? "0.6rem" : "0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
            <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "#0f1724" }}>
              {agenda.agendaDate ? formatDateShort(agenda.agendaDate) : "—"}
              <span style={{ paddingLeft:"0.2rem", fontSize: "0.75rem", color: "#475569" }}>
                {agenda.agendaDateEnd ? ` s/d ${formatDateShort(agenda.agendaDateEnd)}` : ""}
              </span>
            </div>
            <div style={{ alignItems: "center", gap: "0.5rem" }}>
              {areaLabel && (
                <div style={{
                  display: "inline-block",
                  padding: "0.35rem 0.25rem",
                  borderRadius: "0.375rem",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  lineHeight: 1,
                  background: "#e8f0ff",
                  color: "#133b8a"
                }}>
                  {areaLabel}
                </div>
              )}

              <div style={{ paddingTop: "0.2rem", paddingLeft: "0.2rem", fontSize: "1rem", fontWeight: 800, color: "#0b63a8", lineHeight: 1.1 }}>
                {agenda.subject_zh || "(Tanpa judul)"}
                {agenda.subject && (
                  <div>{agenda.subject}</div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>

            {/* ✅ Download link */}
            {lastExportUrl && (
              <a
                href={lastExportUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: "0.8rem",
                  padding: "0.35rem 0.6rem",
                  borderRadius: "0.5rem",
                  border: "1px solid #1f64d0",
                  background: "#e8f0ff",
                  color: "#1f64d0",
                  fontWeight: 600,
                  textDecoration: "none",
                  whiteSpace: "nowrap"
                }}
              >
                Download Sheet
              </a>
            )}

            {/* existing mobile toggle */}
            {isMobile && (
              !disabled && (
                <button
                  className="btn"
                  onClick={() => setExpanded(e => !e)}
                  style={{
                    fontSize: "0.85rem",
                    padding: "0.4rem 0.6rem",
                    borderRadius: "8px"
                  }}
                  aria-expanded={expanded}
                >
                  {expanded ? "Hide details" : "Show details"}
                </button>
              )
            )}
          </div>
        </div>

        {/* right-top small range / hours info (kept but small) */}
        {expanded && (agenda.agendaDateEnd || agenda.hoursInfo) && (
          <div style={{ marginTop: "0.4rem", textAlign: "right", fontSize: "0.75rem", color: "#475569" }}>
            {agenda.hoursInfo ? ` • ${agenda.hoursInfo}` : ""}
          </div>
        )}
      </div>

      {/* COLLAPSIBLE BODY: hidden on mobile until expanded */}
      {(!isMobile || expanded) && (
        <>
          {/* pre-registration breakdown (agenda level) */}
          {(() => {
            const pesertaTotal = (preTotals.pesertaMale || 0) + (preTotals.pesertaFemale || 0);
            const petugasTotal = (preTotals.petugasMale || 0) + (preTotals.petugasFemale || 0);

            // if both empty, don't render the whole pre-registration block
            if (pesertaTotal === 0 && petugasTotal === 0) return null;

            return (
              <div style={{ marginBottom: "0.6rem", fontSize: "0.875rem", color: "#444" }}>
                <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Pre-registrations</div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: (pesertaTotal > 0 && petugasTotal > 0) ? "1fr 1fr" : "1fr",
                  gap: "0.5rem"
                }}>
                  {pesertaTotal > 0 && (
                    <div>
                      <div style={{ fontSize: "0.85rem" }}><strong>Peserta</strong></div>
                      <div style={{ color: "#666", fontSize: "0.85rem" }}>Pria: {preTotals.pesertaMale || 0}</div>
                      <div style={{ color: "#666", fontSize: "0.85rem" }}>Wanita: {preTotals.pesertaFemale || 0}</div>
                    </div>
                  )}

                  {petugasTotal > 0 && (
                    <div>
                      <div style={{ fontSize: "0.85rem" }}><strong>Petugas</strong></div>
                      <div style={{ color: "#666", fontSize: "0.85rem" }}>Pria: {preTotals.petugasMale || 0}</div>
                      <div style={{ color: "#666", fontSize: "0.85rem" }}>Wanita: {preTotals.petugasFemale || 0}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* totals */}
          {showDonation && events.length > 1 && (
          <div style={{ marginBottom: "0.6rem", fontSize: "0.875rem" }}>
            <div style={{ marginTop: "0.25rem", fontWeight: 700, color: "#2b6cb0" }}>
              Total Donasi: {Number(totals.donationTotal || 0).toLocaleString()}
            </div>
          </div>
          )}

          {/* per-event list */}
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {events.map((ev) => {
              const s = perEvent[ev.id] || {};
              const isActive = selectedEventId === ev.id;
              return (
                <div
                  key={ev.id}
                  onClick={(e) => { e.stopPropagation(); onSelectEvent && onSelectEvent(ev.id); }}
                  role="button"
                  tabIndex={0}
                  style={{
                    padding: "0.6rem",
                    borderRadius: "0.45rem",
                    background: isActive ? "#e9f2ff" : "#fff",
                    border: isActive ? "1px solid #2b6cb0" : "1px solid #f0f0f0",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.35rem",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{ev.eventDate}</div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                    <div>
                      <div style={{ fontSize: "0.85rem" }}><strong>Peserta</strong></div>
                      <div style={{ color: "#666", fontSize: "0.85rem" }}>Pria: {s.pesertaMale || 0}</div>
                      <div style={{ color: "#666", fontSize: "0.85rem" }}>Wanita: {s.pesertaFemale || 0}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.85rem" }}><strong>Petugas</strong></div>
                      <div style={{ color: "#666", fontSize: "0.85rem" }}>Pria: {s.petugasMale || 0}</div>
                      <div style={{ color: "#666", fontSize: "0.85rem" }}>Wanita: {s.petugasFemale || 0}</div>
                    </div>
                  </div>
                  {showDonation && (
                  <div style={{ fontWeight: 700, color: "#2b6cb0", fontSize: "0.9rem" }}>
                    Donasi: {Number(s.donationTotal || 0).toLocaleString()}
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

PresensiCardAgendaInfo.propTypes = {
  agenda: PropTypes.object.isRequired,
  selectedEventId: PropTypes.string,
  onSelectEvent: PropTypes.func,
  showDonation: PropTypes.bool
};
